/**
 * "Rotate your phone" — shown on a handset held sideways, over everything.
 *
 * Portrait is the mobile target and the Discord bridge locks it, so the only way to reach a
 * landscape phone is the open web. The layout there is not merely tight: `solveLayout` piles seats
 * 0 and 1 on top of each other at 844x390, and no fix for that is planned. Asking for a quarter
 * turn is a better answer than showing a table with two players in the same place.
 *
 * The test is deliberately narrow. `pointer: coarse` keeps it away from a touchscreen laptop or a
 * desktop browser with a short window, and the 480px height ceiling keeps it away from tablets,
 * which have the room to lay out properly.
 */
import { useEffect, useState } from 'react';

export interface Viewportish {
  width: number;
  height: number;
  coarsePointer: boolean;
}

/** A phone on its side, and nothing else. */
export function shouldAskToRotate(v: Viewportish): boolean {
  return v.coarsePointer && v.width > v.height && v.height < 480;
}

function read(): Viewportish {
  const coarse = typeof matchMedia === 'function' ? matchMedia('(pointer: coarse)').matches : false;
  return { width: window.innerWidth, height: window.innerHeight, coarsePointer: coarse };
}

/**
 * `force` shows the hint whatever the viewport says, and exists for one caller: the Design Lab.
 * The test below is deliberately narrow — a coarse pointer on a short landscape window — and a
 * desktop browser can satisfy neither half, so without this seam the hint is the one surface in the
 * game that cannot be looked at while it is being designed. Nothing in the app passes it.
 */
export function RotateHint({ force = false }: { force?: boolean }) {
  const [v, setV] = useState<Viewportish | null>(null);

  useEffect(() => {
    const update = (): void => setV(read());
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  if (!force && !(v && shouldAskToRotate(v))) return null;

  return (
    <div className="rotate-hint" data-testid="rotate-hint" role="status" aria-live="polite">
      <div className="rotate-hint__inner">
        <div className="rotate-hint__glyph" aria-hidden>
          ⟳
        </div>
        <h1 className="display display--lg">Rotate your phone</h1>
        <p className="muted">Spades Night is built for portrait. Turn it upright and the table comes back.</p>
      </div>
    </div>
  );
}
